import { useCallback, useEffect, useMemo, useState } from "react";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableHead,
    TableCell,
} from "@/components/ui/table";
import {
    Headphones,
    CalendarDays,
    TrendingUp,
    ShoppingBag,
    Receipt,
    Truck,
    AlertTriangle,
    CheckCircle2,
    Users,
    Building2,
    ArrowUp,
    ArrowDown,
    ArrowUpDown,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { OPS } from "@/constants/testIds";
import { EmptyState } from "@/components/common/EmptyState";
import { monthOf } from "@/components/common/DataScope";
import { fmtNumber, fmtInt, currencySuffix, isoFromDate } from "@/lib/format";

const CC = OPS.callCenter;

// ---------------------------------------------------------------- helpers
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const slug = (s) =>
    String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "x";

const AMBER = { color: "#B45309", bg: "rgba(180,83,9,0.10)" };

// Average invoice value for one agent. Computed from the row when the backend
// hasn't sent it (older cached response), so the column is never blank.
const agentAov = (a) => {
    if (typeof a?.avg_order_value === "number") return a.avg_order_value;
    const o = a?.orders || 0;
    return o ? (a?.revenue || 0) / o : 0;
};

// Sortable table head. Numbers default to high→low, names to A→Z.
function SortHead({ col, label, align, sort, onSort }) {
    const active = sort.col === col;
    const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
    return (
        <TableHead className={align === "end" ? "text-end" : "text-start"}>
            <button
                type="button"
                data-testid={CC.agentSort(col)}
                onClick={() => onSort((s) => ({
                    col,
                    dir: s.col === col ? (s.dir === "asc" ? "desc" : "asc") : (col === "agent" ? "asc" : "desc"),
                }))}
                className={`inline-flex items-center gap-1 transition-colors hover:text-[color:var(--brand-brown)] ${
                    active ? "font-bold text-[color:var(--ink)]" : "text-[color:var(--ink-muted)]"
                } ${align === "end" ? "flex-row-reverse" : ""}`}
            >
                {label}
                <Icon className="h-3 w-3 shrink-0 opacity-70" />
            </button>
        </TableHead>
    );
}

function defaultRange() {
    const today = new Date();
    return { from: isoFromDate(addDays(today, -6)), to: isoFromDate(today) };
}

// money: latin numerals + localized currency suffix
function Money({ value, lang, decimals = 2 }) {
    return (
        <span className="tnum">
            <bdi dir="ltr" className="font-extrabold">{fmtNumber(value, lang, decimals)}</bdi>{" "}
            <span className="text-sm font-medium text-[color:var(--ink-muted)]">{currencySuffix(lang)}</span>
        </span>
    );
}

// ---------------------------------------------------------------- date field
function DateField({ testId, label, value, onChange }) {
    const { t } = useI18n();
    const [open, setOpen] = useState(false);
    const parse = (s) => {
        if (!s) return undefined;
        const [y, m, d] = s.split("-").map(Number);
        return new Date(y, m - 1, d);
    };
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    data-testid={testId}
                    variant="outline"
                    className="h-9 justify-start gap-2 font-normal w-full lg:w-auto"
                    aria-label={label}
                >
                    <CalendarDays className="h-4 w-4 text-[color:var(--brand-brown)] shrink-0" />
                    <span className="text-[10px] font-medium text-[color:var(--ink-muted)] hidden lg:inline">{label}:</span>
                    <bdi dir="ltr" className="truncate">{value || t("ss_pick_date")}</bdi>
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                    mode="single"
                    selected={parse(value)}
                    onSelect={(d) => {
                        if (d) onChange(isoFromDate(d));
                        setOpen(false);
                    }}
                    initialFocus
                />
            </PopoverContent>
        </Popover>
    );
}

// ---------------------------------------------------------------- KPI card
function KpiCard({ k, label, icon: Icon, children, accent }) {
    return (
        <Card
            data-testid={CC.kpiCard(k)}
            className="surface border-[color:var(--border-soft)] rounded-2xl shadow-[var(--shadow-sm)]"
        >
            <CardContent className="p-5">
                <div className="flex items-center gap-2 text-[color:var(--ink-muted)]">
                    <span
                        className="flex h-8 w-8 items-center justify-center rounded-lg"
                        style={{ backgroundColor: accent ? "rgba(209,128,47,0.12)" : "rgba(186,217,235,0.5)" }}
                    >
                        <Icon className="h-4 w-4 text-[color:var(--brand-brown)]" />
                    </span>
                    <span className="text-sm font-medium">{label}</span>
                </div>
                <div className="mt-3 kpi-number text-3xl font-extrabold text-[color:var(--ink)]">
                    {children}
                </div>
            </CardContent>
        </Card>
    );
}

// ---------------------------------------------------------------- page
export default function CallCenterPage() {
    const { t, lang } = useI18n();
    const init = useMemo(() => defaultRange(), []);
    const [from, setFrom] = useState(init.from);
    const [to, setTo] = useState(init.to);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const load = useCallback(async (f, tt) => {
        setLoading(true);
        setError(false);
        try {
            const r = await api.get(`/call-center/summary?from=${f}&to=${tt}`);
            setData(r.data);
        } catch (e) {
            setError(true);
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load(from, to);
    }, [from, to, load]);

    const applyPreset = (days) => {
        const today = new Date();
        setFrom(isoFromDate(addDays(today, -(days - 1))));
        setTo(isoFromDate(today));
    };

    const overall = data?.overall || {};
    const byBranch = data?.by_branch || [];
    const byAgent = useMemo(() => data?.by_agent || [], [data]);
    const [agentSort, setAgentSort] = useState({ col: "orders", dir: "desc" });
    const sortedAgents = useMemo(() => {
        const rows = [...byAgent];
        const { col, dir } = agentSort;
        const mul = dir === "asc" ? 1 : -1;
        rows.sort((a, b) => {
            if (col === "agent") return mul * String(a.agent || "").localeCompare(String(b.agent || ""), "ar");
            const va = col === "avg_order_value" ? agentAov(a) : (a[col] || 0);
            const vb = col === "avg_order_value" ? agentAov(b) : (b[col] || 0);
            return mul * (va - vb);
        });
        return rows;
    }, [byAgent, agentSort]);
    const unpaidOrders = data?.total_unpaid_orders || 0;
    const unpaidRevenue = data?.total_unpaid_revenue || 0;
    const avgDeliveryFee = Number(overall.avg_delivery_fee || 0);

    const hasAnyData =
        (overall.total_orders || 0) > 0 || unpaidOrders > 0 || byBranch.length > 0;

    const isLowDelivery = (b) => {
        if (!b.orders || avgDeliveryFee <= 0) return false;
        return b.delivery_total / b.orders < 0.5 * avgDeliveryFee;
    };

    // ---- range controls (always visible) ----
    const Controls = (
        <>
            <Button
                data-testid={CC.preset("7")}
                variant="soft"
                size="sm"
                onClick={() => applyPreset(7)}
                className="w-full lg:w-auto"
            >
                {t("cc_preset_7")}
            </Button>
            <Button
                data-testid={CC.preset("30")}
                variant="soft"
                size="sm"
                onClick={() => applyPreset(30)}
                className="w-full lg:w-auto"
            >
                {t("cc_preset_30")}
            </Button>
            <DateField testId={CC.rangeFrom} label={t("ss_from")} value={from} onChange={setFrom} />
            <DateField testId={CC.rangeTo} label={t("ss_to")} value={to} onChange={setTo} />
        </>
    );

    return (
        <div data-testid={CC.page} className="fin-scope space-y-5 sm:space-y-6">
            <PageHeader
                icon={Headphones}
                title={t("cc_title")}
                subtitle={t("cc_subtitle")}
                actions={Controls}
            />

            {/* loading */}
            {loading ? (
                <div data-testid={CC.loading} className="space-y-5">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <Skeleton className="h-28 w-full rounded-2xl" />
                        <Skeleton className="h-28 w-full rounded-2xl" />
                        <Skeleton className="h-28 w-full rounded-2xl" />
                    </div>
                    <Skeleton className="h-32 w-full rounded-2xl" />
                    <Skeleton className="h-64 w-full rounded-2xl" />
                </div>
            ) : error ? (
                <Card data-testid={CC.errorState} className="surface border-[color:var(--border-soft)] rounded-2xl">
                    <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
                        <AlertTriangle className="h-8 w-8 text-[color:var(--danger)]" />
                        <p className="text-sm text-[color:var(--ink-muted)]">{t("cc_error")}</p>
                        <Button
                            data-testid={CC.retry}
                            onClick={() => load(from, to)}
                            className="bg-[color:var(--brand-orange)] text-white hover:bg-[color:var(--brand-orange-hover)]"
                        >
                            {t("fa_retry")}
                        </Button>
                    </CardContent>
                </Card>
            ) : !hasAnyData ? (
                <Card className="surface border-[color:var(--border-soft)] rounded-2xl">
                    <CardContent className="py-8">
                        <EmptyState
                            testId={CC.empty}
                            icon={Headphones}
                            title={t("cc_empty")}
                            dataset="call_center_orders"
                            month={monthOf(to)}
                        />
                    </CardContent>
                </Card>
            ) : (
                <>
                    {/* 1) headline KPIs */}
                    <div data-testid={CC.kpi} className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <div className="space-y-2">
                            <KpiCard k="revenue" label={t("cc_kpi_revenue")} icon={TrendingUp} accent>
                                <Money value={overall.total_revenue || 0} lang={lang} />
                            </KpiCard>
                            <p
                                data-testid={CC.revenueNote}
                                className="px-1 text-xs leading-relaxed text-[color:var(--ink-muted)]"
                            >
                                {t("cc_revenue_note")}
                            </p>
                        </div>
                        <KpiCard k="orders" label={t("cc_kpi_orders")} icon={ShoppingBag}>
                            <bdi dir="ltr" className="tnum">{fmtInt(overall.total_orders || 0, lang)}</bdi>
                        </KpiCard>
                        <KpiCard k="aov" label={t("cc_kpi_aov")} icon={Receipt}>
                            <Money value={overall.avg_order_value || 0} lang={lang} />
                        </KpiCard>
                    </div>

                    {/* 2) delivery-fee health (prominent, red) */}
                    <Card
                        data-testid={CC.deliveryPanel}
                        className="overflow-hidden rounded-2xl shadow-[var(--shadow-sm)]"
                        style={{ border: "1.5px solid var(--brand-brown)" }}
                    >
                        <CardHeader
                            className="flex flex-row items-center gap-2 space-y-0 py-3"
                            style={{ backgroundColor: "rgba(143,52,12,0.08)" }}
                        >
                            <Truck className="h-5 w-5 text-[color:var(--brand-brown)]" />
                            <div>
                                <CardTitle className="text-base font-bold text-[color:var(--brand-brown)]">
                                    {t("cc_delivery_title")}
                                </CardTitle>
                                <p className="text-xs text-[color:var(--brand-brown)] opacity-80">{t("cc_delivery_sub")}</p>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4 py-5">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div className="rounded-xl surface-2 px-4 py-3">
                                    <p className="text-xs text-[color:var(--ink-muted)]">{t("cc_delivery_total")}</p>
                                    <p className="mt-1 text-2xl font-extrabold text-[color:var(--ink)]">
                                        <Money value={overall.total_delivery || 0} lang={lang} />
                                    </p>
                                </div>
                                <div className="rounded-xl surface-2 px-4 py-3">
                                    <p className="text-xs text-[color:var(--ink-muted)]">{t("cc_delivery_avg")}</p>
                                    <p className="mt-1 text-2xl font-extrabold text-[color:var(--ink)]">
                                        <Money value={avgDeliveryFee} lang={lang} />
                                    </p>
                                </div>
                            </div>
                            <div
                                data-testid={CC.deliveryWarning}
                                className="flex items-start gap-2 rounded-xl px-4 py-3 text-sm font-medium"
                                style={{ backgroundColor: "rgba(143,52,12,0.08)", color: "var(--brand-brown)" }}
                            >
                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                <span>
                                    {t("cc_delivery_warn_pre")}{" "}
                                    <bdi dir="ltr" className="tnum font-bold">
                                        {fmtNumber(avgDeliveryFee, lang, 2)} {currencySuffix(lang)}
                                    </bdi>{" "}
                                    {t("cc_delivery_warn_post")}
                                </span>
                            </div>
                        </CardContent>
                    </Card>

                    {/* 3) unpaid flag */}
                    {unpaidOrders > 0 ? (
                        <div
                            data-testid={CC.unpaidFlag}
                            className="flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold"
                            style={{ borderColor: "var(--danger)", backgroundColor: "rgba(180,35,24,0.08)", color: "var(--danger)" }}
                        >
                            <AlertTriangle className="h-4 w-4 shrink-0" />
                            <span>
                                {t("cc_unpaid_label")}: <bdi dir="ltr" className="tnum">{fmtInt(unpaidOrders, lang)}</bdi>{" "}
                                {t("cc_orders_word")},{" "}
                                <bdi dir="ltr" className="tnum">{fmtNumber(unpaidRevenue, lang, 2)} {currencySuffix(lang)}</bdi>
                            </span>
                        </div>
                    ) : (
                        <div
                            data-testid={CC.unpaidFlag}
                            className="flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold"
                            style={{ borderColor: "var(--success)", backgroundColor: "rgba(31,122,77,0.08)", color: "var(--success)" }}
                        >
                            <CheckCircle2 className="h-4 w-4 shrink-0" />
                            <span>{t("cc_all_paid")}</span>
                        </div>
                    )}

                    {/* 4) by branch */}
                    <Card className="surface border-[color:var(--border-soft)] rounded-2xl shadow-[var(--shadow-sm)]">
                        <CardHeader className="flex flex-row items-center gap-2 space-y-0 pb-3">
                            <Building2 className="h-5 w-5 text-[color:var(--brand-brown)]" />
                            <CardTitle className="text-lg font-bold text-[color:var(--ink)]">{t("cc_by_branch_title")}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="overflow-x-auto">
                                <Table data-testid={CC.branchTable}>
                                    <TableHeader>
                                        <TableRow className="hover:bg-transparent">
                                            <TableHead className="text-start text-[color:var(--ink-muted)]">{t("branch")}</TableHead>
                                            <TableHead className="text-end text-[color:var(--ink-muted)]">{t("revenue_col")}</TableHead>
                                            <TableHead className="text-end text-[color:var(--ink-muted)]">{t("orders_col")}</TableHead>
                                            <TableHead className="text-end text-[color:var(--ink-muted)]">{t("cc_col_aov")}</TableHead>
                                            <TableHead className="text-end text-[color:var(--ink-muted)]">{t("cc_col_delivery")}</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {byBranch.map((b) => {
                                            const low = isLowDelivery(b);
                                            return (
                                                <TableRow
                                                    key={b.branch_id}
                                                    data-testid={CC.branchRow(b.branch_id)}
                                                    style={low ? { backgroundColor: AMBER.bg } : undefined}
                                                >
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-semibold text-[color:var(--ink)]">
                                                                {b.branch_name || <bdi dir="ltr">#{b.branch_id}</bdi>}
                                                            </span>
                                                            {low ? (
                                                                <Badge
                                                                    variant="secondary"
                                                                    className="gap-1 border-0 text-[10px]"
                                                                    style={{ color: AMBER.color, backgroundColor: "rgba(180,83,9,0.16)" }}
                                                                >
                                                                    <Truck className="h-3 w-3" />
                                                                    {t("cc_low_delivery")}
                                                                </Badge>
                                                            ) : null}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-end tnum font-semibold text-[color:var(--ink)]">
                                                        <bdi dir="ltr">{fmtNumber(b.revenue || 0, lang, 2)}</bdi>
                                                    </TableCell>
                                                    <TableCell className="text-end tnum text-[color:var(--ink)]">
                                                        <bdi dir="ltr">{fmtInt(b.orders || 0, lang)}</bdi>
                                                    </TableCell>
                                                    <TableCell className="text-end tnum text-[color:var(--ink-muted)]">
                                                        <bdi dir="ltr">{fmtNumber(b.avg_order_value || 0, lang, 2)}</bdi>
                                                    </TableCell>
                                                    <TableCell
                                                        className="text-end tnum font-medium"
                                                        style={{ color: low ? AMBER.color : "var(--ink)" }}
                                                    >
                                                        <bdi dir="ltr">{fmtNumber(b.delivery_total || 0, lang, 2)}</bdi>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>

                    {/* 5) agents — sortable, every agent, avg invoice value included */}
                    <Card className="surface border-[color:var(--border-soft)] rounded-2xl shadow-[var(--shadow-sm)]">
                        <CardHeader className="flex flex-row items-center gap-2 space-y-0 pb-3">
                            <Users className="h-5 w-5 text-[color:var(--brand-brown)]" />
                            <CardTitle className="text-base font-bold text-[color:var(--ink)]">{t("cc_agents_title")}</CardTitle>
                            {byAgent.length ? (
                                <Badge variant="secondary" className="border-0 text-[10px]" data-testid={CC.agentCount}>
                                    <bdi dir="ltr">{fmtInt(byAgent.length, lang)}</bdi>
                                </Badge>
                            ) : null}
                        </CardHeader>
                        <CardContent>
                            {byAgent.length === 0 ? (
                                <p className="py-6 text-center text-sm text-[color:var(--ink-muted)]">{t("cc_no_agents")}</p>
                            ) : (
                                <div className="overflow-auto max-h-[560px] rounded-lg border border-[color:var(--border-soft)]">
                                    <Table data-testid={CC.agentTable}>
                                        <TableHeader className="sticky top-0 z-10 bg-[color:var(--surface)]">
                                            <TableRow className="hover:bg-transparent">
                                                <SortHead col="agent" align="start" label={t("cc_col_agent")} sort={agentSort} onSort={setAgentSort} />
                                                <SortHead col="orders" align="end" label={t("orders_col")} sort={agentSort} onSort={setAgentSort} />
                                                <SortHead col="revenue" align="end" label={t("revenue_col")} sort={agentSort} onSort={setAgentSort} />
                                                <SortHead col="avg_order_value" align="end" label={t("cc_col_avg_invoice")} sort={agentSort} onSort={setAgentSort} />
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {sortedAgents.map((a, i) => (
                                                <TableRow key={`${slug(a.agent)}-${i}`} data-testid={CC.agentRow(slug(a.agent))}>
                                                    <TableCell className="whitespace-nowrap font-medium text-[color:var(--ink)]">
                                                        {a.agent || <span className="text-[color:var(--ink-muted)]">—</span>}
                                                    </TableCell>
                                                    <TableCell className="text-end tnum text-[color:var(--ink)]">
                                                        <bdi dir="ltr">{fmtInt(a.orders || 0, lang)}</bdi>
                                                    </TableCell>
                                                    <TableCell className="text-end tnum text-[color:var(--ink-muted)]">
                                                        <bdi dir="ltr">{fmtNumber(a.revenue || 0, lang, 2)}</bdi>
                                                    </TableCell>
                                                    <TableCell className="text-end tnum font-semibold text-[color:var(--ink)]">
                                                        <bdi dir="ltr">{fmtNumber(agentAov(a), lang, 2)}</bdi>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
}
